import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useToast } from '@caos/ca-component-library';
import { fetchAllStars, toggleStar as apiToggleStar, BulkStarData } from '../lib/marketplace';

interface StarsContextValue {
  counts: Record<string, number>;
  userStars: Set<string>;
  loading: boolean;
  toggle: (itemId: string, itemType: 'skill' | 'mcp-server') => void;
  pendingItems: Set<string>;
}

const StarsContext = createContext<StarsContextValue | null>(null);

export function StarsProvider({ children }: { children: React.ReactNode }) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [userStars, setUserStars] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [pendingItems, setPendingItems] = useState<Set<string>>(new Set());
  const { showToast } = useToast();

  useEffect(() => {
    fetchAllStars()
      .then((data: BulkStarData) => {
        setCounts(data.counts);
        setUserStars(new Set(data.userStars));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = useCallback((itemId: string, itemType: 'skill' | 'mcp-server') => {
    if (pendingItems.has(itemId)) return;

    const wasStarred = userStars.has(itemId);
    const prevCount = counts[itemId] ?? 0;

    setPendingItems((prev) => new Set(prev).add(itemId));
    if (wasStarred) {
      setUserStars((prev) => { const next = new Set(prev); next.delete(itemId); return next; });
      setCounts((prev) => ({ ...prev, [itemId]: Math.max(0, prevCount - 1) }));
    } else {
      setUserStars((prev) => new Set(prev).add(itemId));
      setCounts((prev) => ({ ...prev, [itemId]: prevCount + 1 }));
    }

    apiToggleStar(itemId, itemType)
      .then((result) => {
        setCounts((prev) => ({ ...prev, [itemId]: result.starCount }));
        if (result.starred) {
          setUserStars((prev) => new Set(prev).add(itemId));
        } else {
          setUserStars((prev) => { const next = new Set(prev); next.delete(itemId); return next; });
        }
      })
      .catch(() => {
        if (wasStarred) {
          setUserStars((prev) => new Set(prev).add(itemId));
        } else {
          setUserStars((prev) => { const next = new Set(prev); next.delete(itemId); return next; });
        }
        setCounts((prev) => ({ ...prev, [itemId]: prevCount }));
        showToast({ message: 'Failed to update star', type: 'error', duration: 3000 });
      })
      .finally(() => {
        setPendingItems((prev) => { const next = new Set(prev); next.delete(itemId); return next; });
      });
  }, [counts, userStars, pendingItems, showToast]);

  return (
    <StarsContext.Provider value={{ counts, userStars, loading, toggle, pendingItems }}>
      {children}
    </StarsContext.Provider>
  );
}

export function useStars() {
  const ctx = useContext(StarsContext);
  if (!ctx) throw new Error('useStars must be used within StarsProvider');
  return ctx;
}
